import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaBirthdayCake, FaCreditCard, FaEnvelope, FaGraduationCap, FaPhone, FaTimes, FaUser } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import './WorkshopBookingForm.css';

const WorkshopBookingForm = ({ workshop, isOpen, onClose, onBookingSuccess }) => {
  const settings = useSelector((state) => state.settings.data);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    age: '',
    skillLevel: 'Beginner',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [errors, setErrors] = useState({});
  const [razorpayKey, setRazorpayKey] = useState(null);


  // Load Razorpay key from settings
  useEffect(() => {
    const loadRazorpayKey = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/settings`);
        const keyId = response.data.data?.paymentApi?.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
        setRazorpayKey(keyId || null);
      } catch (error) {
        console.error('Error loading Razorpay key:', error);
        // Fallback to environment variable if settings endpoint fails
        const envKey = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
        setRazorpayKey(envKey || null);
      }
    };
    
    if (isOpen && workshop?.price > 0) {
      loadRazorpayKey();
    } else if (isOpen && (!workshop?.price || workshop.price === 0)) {
      setRazorpayKey(null); // Free workshop, no payment needed
    }
  }, [isOpen, workshop, API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/[\s-]/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else if (parseInt(formData.age) < 1 || parseInt(formData.age) > 120) {
      newErrors.age = 'Please enter a valid age (1-120)';
    }

    if (!formData.skillLevel) {
      newErrors.skillLevel = 'Please select your skill level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const initiatePayment = async (bookingId) => {
    if (!window.Razorpay || !razorpayKey) {
      toast.error('Payment gateway not available. Please refresh and try again.');
      return;
    }

    try {
      // Validate workshop price
      if (!workshop.price || workshop.price <= 0) {
        toast.error('Invalid workshop price. Please contact support.');
        setProcessingPayment(false);
        return;
      }

      // Create Razorpay order
      const orderResponse = await axios.post(`${API_URL}/api/razorpay/create-workshop-order`, {
        amount: workshop.price,
        currency: workshop.currency || 'INR',
        bookingId: bookingId,
        workshopId: workshop._id
      });

      if (!orderResponse.data.success || !orderResponse.data.data) {
        toast.error(orderResponse.data.message || 'Failed to initialize payment');
        setProcessingPayment(false);
        return;
      }

      const { orderId, amount, currency } = orderResponse.data.data;

      if (!orderId) {
        toast.error('Invalid payment order. Please try again.');
        setProcessingPayment(false);
        return;
      }

      // Razorpay options
      const options = {
        key: razorpayKey,
        amount: amount, // Amount in paise
        currency: currency,
        name: settings?.companyName || 'Store',
        description: `Workshop: ${workshop.title}`,
        order_id: orderId,
        handler: async (razorpayResponse) => {
          setProcessingPayment(true);
          try {
            // Verify payment
            const verificationResponse = await axios.post(`${API_URL}/api/razorpay/verify-workshop-payment`, {
              razorpayOrderId: razorpayResponse.razorpay_order_id,
              razorpayPaymentId: razorpayResponse.razorpay_payment_id,
              razorpaySignature: razorpayResponse.razorpay_signature,
              bookingId: bookingId
            });

            toast.success('Payment successful! Workshop booking confirmed.');
            
            // Reset form
            setFormData({
              name: '',
              email: '',
              mobile: '',
              age: '',
              skillLevel: 'Beginner',
              notes: ''
            });
            
            // Use booking data from verification response (no need to fetch separately - avoids 401 error)
            const verifiedBooking = verificationResponse.data.data?.booking;
            
            // Close modal and notify parent
            if (onBookingSuccess) {
              // If booking data is in response, use it; otherwise just pass the bookingId
              if (verifiedBooking) {
                onBookingSuccess(verifiedBooking);
              } else {
                // Fallback: create a minimal booking object
                onBookingSuccess({ _id: bookingId, status: 'confirmed', paymentStatus: 'paid' });
              }
            }
            onClose();
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error(error.response?.data?.message || 'Payment verification failed. Please contact support.');
          } finally {
            setProcessingPayment(false);
          }
        },
        prefill: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          contact: formData.mobile.replace(/[\s-]/g, '')
        },
        theme: {
          color: '#8B4513'
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled. Your booking is still pending payment.');
            setProcessingPayment(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description || 'Unknown error'}`);
        setProcessingPayment(false);
      });
      
      razorpay.open();
      setProcessingPayment(true);
    } catch (error) {
      console.error('Payment initiation error:', error);
      
      // Handle specific error cases
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to initiate payment';
        const statusCode = error.response.status;
        
        if (statusCode === 503) {
          toast.error('Payment gateway is not configured. Please contact admin.');
        } else if (statusCode === 400) {
          toast.error(errorMessage);
        } else if (statusCode === 500) {
          toast.error(errorMessage || 'Server error. Please try again later or contact support.');
        } else {
          toast.error(errorMessage);
        }
      } else if (error.request) {
        // Request was made but no response received
        toast.error('Unable to connect to payment server. Please check your internet connection.');
      } else {
        // Something else happened
        toast.error(error.message || 'Failed to initiate payment. Please try again.');
      }
      
      setProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    // If workshop is free (price = 0), create booking without payment
    if (!workshop.price || workshop.price === 0) {
      setSubmitting(true);
      try {
        const response = await axios.post(`${API_URL}/api/workshop-bookings`, {
          workshopId: workshop._id,
          name: formData.name.trim(),
          email: formData.email.trim(),
          mobile: formData.mobile.replace(/[\s-]/g, ''),
          age: parseInt(formData.age),
          skillLevel: formData.skillLevel,
          notes: formData.notes.trim()
        });

        toast.success('Workshop booking submitted successfully!');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          mobile: '',
          age: '',
          skillLevel: 'Beginner',
          notes: ''
        });
        
        // Close modal and notify parent
        if (onBookingSuccess) {
          onBookingSuccess(response.data.data);
        }
        onClose();
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to submit booking';
        toast.error(errorMessage);
        
        // If workshop is full, close modal
        if (error.response?.status === 400 && errorMessage.includes('fully booked')) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // For paid workshops, create booking first, then initiate payment
    setSubmitting(true);

    try {
      // Create booking first (with pending payment status)
      const response = await axios.post(`${API_URL}/api/workshop-bookings`, {
        workshopId: workshop._id,
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.replace(/[\s-]/g, ''),
        age: parseInt(formData.age),
        skillLevel: formData.skillLevel,
        notes: formData.notes.trim()
      });

      const bookingId = response.data.data._id;
      setSubmitting(false);
      
      // Initiate payment
      await initiatePayment(bookingId);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit booking';
      toast.error(errorMessage);
      
      // If workshop is full, close modal
      if (error.response?.status === 400 && errorMessage.includes('fully booked')) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: '',
        email: '',
        mobile: '',
        age: '',
        skillLevel: 'Beginner',
        notes: ''
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="booking-modal-overlay" onClick={handleClose}>
      <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h2 className="booking-modal-title">Book Workshop</h2>
          <button
            className="booking-modal-close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="booking-modal-body">
          <div className="booking-workshop-info">
            <h3 className="workshop-name">{workshop.title}</h3>
            <div className="workshop-details">
              {workshop.date && (
                <p>📅 {new Date(workshop.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</p>
              )}
              {workshop.time && <p>🕐 {workshop.time}</p>}
              <p className="workshop-price">
                💰 {workshop.currency} {workshop.price}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="booking-form">
            <div className="form-group">
              <label htmlFor="name">
                <FaUser className="label-icon" />
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.name ? 'error' : ''}
                disabled={submitting}
                required
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <FaEnvelope className="label-icon" />
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className={errors.email ? 'error' : ''}
                disabled={submitting}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="mobile">
                <FaPhone className="label-icon" />
                Mobile Number *
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                maxLength="10"
                className={errors.mobile ? 'error' : ''}
                disabled={submitting}
                required
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="age">
                  <FaBirthdayCake className="label-icon" />
                  Age *
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="Your age"
                  min="1"
                  max="120"
                  className={errors.age ? 'error' : ''}
                  disabled={submitting}
                  required
                />
                {errors.age && <span className="error-message">{errors.age}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="skillLevel">
                  <FaGraduationCap className="label-icon" />
                  Skill Level *
                </label>
                <select
                  id="skillLevel"
                  name="skillLevel"
                  value={formData.skillLevel}
                  onChange={handleChange}
                  className={errors.skillLevel ? 'error' : ''}
                  disabled={submitting}
                  required
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
                {errors.skillLevel && <span className="error-message">{errors.skillLevel}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any special requirements or questions..."
                rows="3"
                disabled={submitting}
              />
            </div>

            <div className="booking-modal-footer">
              <button
                type="button"
                onClick={handleClose}
                className="btn-cancel"
                disabled={submitting || processingPayment}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting || processingPayment || (workshop.price > 0 && !razorpayKey)}
              >
                {processingPayment ? (
                  <>
                    <FaCreditCard /> Processing Payment...
                  </>
                ) : submitting ? (
                  'Creating Booking...'
                ) : !workshop.price || workshop.price === 0 ? (
                  'Confirm Booking'
                ) : !razorpayKey ? (
                  'Payment Gateway Not Available'
                ) : (
                  <>
                    <FaCreditCard /> Pay & Book ({workshop.currency || '₹'}{workshop.price})
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkshopBookingForm;

